import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CoinIcon } from "@/components/Icons";

export default function PaymentResult() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: "CHECKING" });
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) { setState({ status: "FAILED" }); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/${orderId}`);
        if (data.status === "PAID") {
          setState({ status: "PAID", credited: data.coins_credited });
          if (data.wallet_coins != null) setUser((u) => (u ? { ...u, coins: data.wallet_coins } : u));
          return;
        }
        if (data.status === "FAILED" || data.status === "CREATE_FAILED") {
          setState({ status: "FAILED" });
          return;
        }
        setState({ status: tries.current > 8 ? "PENDING" : "CHECKING" });
      } catch (e) {
        if (e?.response?.status === 404) { setState({ status: "FAILED" }); return; }
      }
      if (tries.current < 24) setTimeout(poll, 2500);
      else setState((s) => (s.status === "PAID" ? s : { status: "PENDING" }));
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const S = state.status;
  return (
    <div className="min-h-screen-dvh max-w-md mx-auto bg-[#0B0C14] flex flex-col items-center justify-center px-8 text-center" data-testid="payment-result-page">
      {S === "CHECKING" && (
        <>
          <div className="w-14 h-14 rounded-full border-4 border-[#E6B84C] border-t-transparent animate-spin mb-5" />
          <p className="text-white font-extrabold font-display">Confirming your payment…</p>
          <p className="text-zinc-500 text-xs mt-1.5">Hang tight, this only takes a few seconds</p>
        </>
      )}
      {S === "PAID" && (
        <>
          <CheckCircle2 size={64} className="text-emerald-400 mb-4 gift-pop" />
          <p className="text-white text-xl font-extrabold font-display" data-testid="payment-success">Payment successful! 🎉</p>
          <p className="text-[#FDE08B] font-bold mt-2 flex items-center gap-1.5 justify-center">
            <CoinIcon size={16} /> {state.credited?.toLocaleString("en-IN")} coins added to your wallet
          </p>
          <button data-testid="payment-back-wallet" onClick={() => navigate("/wallet")}
            className="mt-7 gradient-primary text-white text-sm font-extrabold px-8 py-3 rounded-full active:scale-95 transition-transform">
            Back to Wallet
          </button>
        </>
      )}
      {S === "PENDING" && (
        <>
          <Clock size={60} className="text-amber-400 mb-4" />
          <p className="text-white text-lg font-extrabold font-display" data-testid="payment-pending">Payment is processing</p>
          <p className="text-zinc-500 text-xs mt-2 max-w-[260px]">Your bank is confirming the payment. Coins will be added automatically — check your wallet in a minute.</p>
          <button data-testid="payment-check-wallet" onClick={() => navigate("/wallet")}
            className="mt-6 bg-white/10 text-white text-sm font-bold px-8 py-3 rounded-full">Go to Wallet</button>
        </>
      )}
      {S === "FAILED" && (
        <>
          <XCircle size={60} className="text-rose-500 mb-4" />
          <p className="text-white text-lg font-extrabold font-display" data-testid="payment-failed">Payment didn't go through</p>
          <p className="text-zinc-500 text-xs mt-2">No money was taken for this order. You can try again.</p>
          <button data-testid="payment-retry" onClick={() => navigate("/wallet")}
            className="mt-6 gradient-primary text-white text-sm font-extrabold px-8 py-3 rounded-full active:scale-95 transition-transform">
            Try Again
          </button>
        </>
      )}
    </div>
  );
}
